import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filter16 } from '@carbon/icons-react';
import PropTypes from 'prop-types';
import { Button } from 'carbon-components-react';
import Panel from './panel';
import Utils from 'utilities/helper.util';
import {
  setReferralSearchFilter,
  setReferralPresetSearchValues,
  selectReferralPresetSearchValues
} from 'redux/referrals';
import { ReferralSearchInitialValues } from 'types';
import { useComponentMount } from 'utilities/hooks.util';
import { isSafari, isMobile } from 'react-device-detect';
import { setMapSearchWithExtentZoom } from 'redux/map/actions';
import { setDashboardUIStateAdminRangeText } from 'redux/dashboard/actions';
import dayjs from 'dayjs';

const initialValues: ReferralSearchInitialValues = {
  enquiryLodged: 'today',
  lastUpdated: '',
  jobStart: '',
  jobStartCustomed: false,
  createdAfter: Utils.getNewDate().startOf('day'),
  hourCreatedAfter: undefined,
  amPmCreatedAfter: undefined,
  createdBefore: Utils.getNewDate().endOf('day'),
  hourCreatedBefore: undefined,
  amPmCreatedBefore: undefined,
  updatedBefore: undefined,
  hourUpdatedBefore: undefined,
  amPmUpdatedBefore: undefined,
  updatedAfter: undefined,
  hourUpdatedAfter: undefined,
  amPmUpdatedAfter: undefined,
  digStartAfter: undefined,
  digStartBefore: undefined,
  status: [],
  requestorFullName: '',
  requestorEmail: '',
  jobNumber: '',
  referralId: ''
};

const SearchFilter: React.FC<Props> = (props) => {
  const {
    buttonKind,
    buttonClassName,
    buttonDisabled,
    buttonIconDescription,
    buttonTooltipPosition,
    disabledAllExceptApply,
    onApply,
    onOpen,
    open,
    mainContainerRef,
    isListView,
    rangeText
  } = props;

  useEffect(() => {
    switch (rangeText) {
      case 'today':
        initialValues.enquiryLodged = 'today';
        initialValues.createdAfter = Utils.getNewDate().startOf('day');
        initialValues.createdBefore = Utils.getNewDate().endOf('day');
        break;
      case 'this-week':
      case 'last-7-days':
        initialValues.enquiryLodged = '7';
        initialValues.createdAfter = Utils.getDateBeforeDays(6).startOf('day');
        break;
      case 'last-30-days':
        initialValues.enquiryLodged = '30';
        initialValues.createdAfter = Utils.getDateBeforeDays(29).startOf('day');
        break;
      default:
        initialValues.enquiryLodged = 'today';
        initialValues.createdAfter = Utils.getNewDate().startOf('day');
        initialValues.createdBefore = Utils.getNewDate().endOf('day');
        break;
    }
  }, [rangeText]);

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<Boolean>(false);
  const [filterValues, setFilterValues] = useState<ReferralSearchInitialValues>(
    initialValues
  );
  const [filterJson, setfilterJson] = useState<string>('');
  const [parentOverflow, setParentOverflow] = useState(false);
  const presetSearchValues = useSelector(selectReferralPresetSearchValues);

  const convertFilterValues = (filterValues: ReferralSearchInitialValues) => {
    const {
      enquiryLodged,
      createdAfter,
      createdBefore,
      hourCreatedAfter,
      hourCreatedBefore,
      amPmCreatedAfter,
      amPmCreatedBefore,
      lastUpdated,
      updatedAfter,
      updatedBefore,
      hourUpdatedAfter,
      hourUpdatedBefore,
      amPmUpdatedAfter,
      amPmUpdatedBefore,
      digStartAfter,
      digStartBefore,
      status,
      requestorFullName,
      requestorEmail,
      jobNumber,
      referralId
    } = filterValues;

    return {
      createdAfter:
        enquiryLodged && createdAfter
          ? enquiryLodged === 'el-custom'
            ? dayjs(
                `${createdAfter.format(
                  'YYYY-MM-DD'
                )} ${hourCreatedAfter} ${amPmCreatedAfter}`,
                'YYYY-MM-DD h:mm A'
              )
                .utc()
                .format()
            : createdAfter.startOf('day').utc().format()
          : '',
      createdBefore:
        enquiryLodged && createdBefore
          ? enquiryLodged === 'el-custom'
            ? dayjs(
                `${createdBefore.format('YYYY-MM-DD')} ${
                  hourCreatedBefore || '11:59'
                } ${amPmCreatedBefore || 'PM'}`,
                'YYYY-MM-DD h:mm A'
              )
                .second(59)
                .utc()
                .format()
            : createdBefore.endOf('day').utc().format()
          : '',
      updatedAfter:
        lastUpdated && updatedAfter
          ? lastUpdated === 'lu-custom'
            ? dayjs(
                `${updatedAfter.format(
                  'YYYY-MM-DD'
                )} ${hourUpdatedAfter} ${amPmUpdatedAfter}`,
                'YYYY-MM-DD h:mm A'
              )
                .utc()
                .format()
            : updatedAfter.startOf('day').utc().format()
          : '',
      updatedBefore:
        lastUpdated && updatedBefore
          ? lastUpdated === 'lu-custom'
            ? dayjs(
                `${updatedBefore.format('YYYY-MM-DD')} ${
                  hourUpdatedBefore || '11:59'
                } ${amPmUpdatedBefore || 'PM'}`,
                'YYYY-MM-DD h:mm A'
              )
                .millisecond(999)
                .utc()
                .format()
            : updatedBefore.endOf('day').utc().format()
          : '',
      digStartAfter: digStartAfter
        ? digStartAfter.startOf('day').utc().format()
        : '',
      digStartBefore: digStartBefore
        ? digStartBefore.endOf('day').utc().format()
        : '',
      requestorFullName,
      requestorEmail,
      status,
      enqExternalId: jobNumber,
      externalId: referralId
    };
  };

  const resetFilterValues = () => {
    dispatch(setDashboardUIStateAdminRangeText(''));

    const initValues = {
      ...initialValues,
      enquiryLodged: 'today',
      createdAfter: Utils.getNewDate().startOf('day'),
      createdBefore: Utils.getNewDate().endOf('day')
    };

    const values = convertFilterValues(initValues);
    !isListView && dispatch(setMapSearchWithExtentZoom([false]));
    setfilterJson(JSON.stringify(values));
    setFilterValues(initValues);
    setIsOpen(!isOpen);
  };

  const applyFilter = (updateValues: any, zoomWithExtent: boolean = false) => {
    dispatch(setDashboardUIStateAdminRangeText(''));
    const values = convertFilterValues(updateValues);
    !isListView && dispatch(setMapSearchWithExtentZoom([zoomWithExtent]));
    setfilterJson(JSON.stringify(values));
    setFilterValues(updateValues);
    setIsOpen(!isOpen);
    if (typeof onApply === 'function') {
      onApply();
    }
  };

  const onClickOpenButton = () => {
    if (typeof open !== 'boolean') {
      setIsOpen(!isOpen);
    }
    if (typeof onOpen === 'function') {
      onOpen();
    }
  };

  const checkParentOverflow = useCallback(() => {
    const mainContainerElement = mainContainerRef.current;
    if (mainContainerElement) {
      if (
        mainContainerElement.scrollHeight > mainContainerElement.clientHeight
      ) {
        setParentOverflow(true);
      }
    }
  }, [mainContainerRef, setParentOverflow]);

  useEffect(() => {
    if (typeof open === 'boolean') {
      setIsOpen(open);
    }
  }, [open]);

  useEffect(() => {
    if (isListView) {
      dispatch(setMapSearchWithExtentZoom([false]));
    }
  }, [isListView, dispatch]);

  useEffect(() => {
    filterJson && dispatch(setReferralSearchFilter(JSON.parse(filterJson)));
  }, [filterJson, dispatch]);

  useEffect(() => {
    if (isOpen && !isMobile && !isSafari) {
      checkParentOverflow();
    } else {
      setParentOverflow(false);
    }
  }, [isOpen, checkParentOverflow, setParentOverflow]);

  useComponentMount(() => {
    const values = convertFilterValues(presetSearchValues || filterValues);
    setfilterJson(JSON.stringify(values));
    presetSearchValues && setFilterValues(presetSearchValues);
    return () => {
      dispatch(setMapSearchWithExtentZoom([true]));
      dispatch(setReferralSearchFilter(undefined));
      dispatch(setReferralPresetSearchValues(undefined));
    };
  });

  return (
    <div className="history-page--search-filter-container">
      <Button
        renderIcon={Filter16}
        iconDescription={buttonIconDescription}
        tooltipPosition={buttonTooltipPosition}
        hasIconOnly
        size="field"
        kind={buttonKind}
        disabled={buttonDisabled}
        className={buttonClassName}
        onClick={onClickOpenButton}
      />
      {isOpen && (
        <Panel
          handleFilter={applyFilter}
          filterValues={filterValues}
          resetValues={resetFilterValues}
          setFilterValues={(v: any) => setFilterValues(v)}
          disabledAllExceptApply={disabledAllExceptApply}
          handleOpenCloseFilter={(open: boolean) => setIsOpen(open)}
          parentOverflow={parentOverflow}
          isListView={isListView}
          rangeText={rangeText}
        />
      )}
    </div>
  );
};

const propTypes = {
  buttonKind: PropTypes.string,
  buttonDisabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttonIconDescription: PropTypes.string,
  buttonTooltipPosition: PropTypes.string,
  onApply: PropTypes.func,
  disabledAllExceptApply: PropTypes.bool,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  mainContainerRef: PropTypes.any,
  isListView: PropTypes.bool,
  rangeText: PropTypes.string
};

type Props = PropTypes.InferProps<typeof propTypes>;

SearchFilter.defaultProps = {
  buttonKind: 'tertiary',
  buttonDisabled: false,
  buttonClassName: '',
  buttonIconDescription: 'Filter',
  buttonTooltipPosition: 'bottom',
  onOpen: () => {}
};

export default React.memo(SearchFilter);
